import { nextTick, readonly, ref } from 'vue';

import { appNavLinks } from './app-navigation-links';

const isMiniAppLoaderVisible = ref(false);

/**
 * iOS WebKit stops painting as soon as `window.location.assign()` is called,
 * so a loader shown in the same tick never reaches the screen. Waiting for
 * `nextTick` (Vue DOM flush) plus two animation frames guarantees the frame
 * with the loader is painted before the navigation starts.
 */
const waitForPaint = () =>
  new Promise<void>(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  });

const hideLoader = () => {
  isMiniAppLoaderVisible.value = false;
};

// When the user comes back from another mini app, iOS restores the page from
// the back-forward cache exactly as it was left — with the loader still on
// screen — so it has to be hidden on `pageshow`
window.addEventListener('pageshow', event => {
  if (event.persisted) {
    hideLoader();
  }
});

const navigateWithLoader = async (navigate: () => void) => {
  isMiniAppLoaderVisible.value = true;

  await nextTick();
  await waitForPaint();

  // Failsafe: if the navigation never happens (e.g. it is blocked by the
  // webview), don't leave the app stuck behind the loader
  setTimeout(hideLoader, 10 * 1000);

  navigate();
};

/**
 * Navigation between mini apps with a full screen loader that stays visible
 * while the webview loads the target app. The loader itself must be rendered
 * by the host app from `isMiniAppLoaderVisible` (e.g. a `FullScreenLoader`
 * at the app root, as in `FreedomToursApp.vue`).
 */
export const useMiniAppNavigation = () => {
  return {
    isMiniAppLoaderVisible: readonly(isMiniAppLoaderVisible),
    openCabinet: (...paths: string[]) =>
      navigateWithLoader(() => appNavLinks.openCabinet(...paths)),
    openAvia: (...paths: string[]) =>
      navigateWithLoader(() => appNavLinks.openAvia(...paths)),
    openRailways: (...paths: string[]) =>
      navigateWithLoader(() => appNavLinks.openRailways(...paths)),
    openHotels: (...paths: string[]) =>
      navigateWithLoader(() => appNavLinks.openHotels(...paths)),
    openTours: (...paths: string[]) =>
      navigateWithLoader(() => appNavLinks.openTours(...paths)),
  };
};
